import { Controller } from "@hotwired/stimulus"

/**
 * Keeps the AEGIS "Model" dropdown in sync with the selected provider radio in
 * team settings.
 *
 * The <select> is server-rendered with every provider's models (each <option>
 * tagged `data-provider`) so it works without JS. When `models-url-value` is
 * present (persisted team), this controller fetches the provider's *live* model
 * list — pulled server-side from the provider API using the team's stored key —
 * and repopulates the dropdown. The server falls back to the static catalogue
 * when there's no key or the upstream call fails, and so do we if the fetch
 * itself errors.
 */
export default class extends Controller {
  static targets = ["provider", "model"]
  static values = { modelsUrl: String, selected: String }

  connect() {
    // Snapshot the server-rendered options as a per-provider fallback before we
    // start mutating the <select>.
    this.staticOptions = {}
    Array.from(this.modelTarget.options).forEach((option) => {
      const provider = option.dataset.provider
      if (!provider) return
      ;(this.staticOptions[provider] ||= []).push({ id: option.value, label: option.text })
    })

    this.cache = {}
    this.loadModels(this.selectedProvider, this.selectedValue)
  }

  providerChanged() {
    // Switching provider invalidates the current choice → prefer that
    // provider's first model.
    this.loadModels(this.selectedProvider, null)
  }

  get selectedProvider() {
    const checked = this.providerTargets.find((radio) => radio.checked)
    return checked ? checked.value : null
  }

  async loadModels(provider, preferred) {
    if (!provider || !this.hasModelTarget) return

    if (this.cache[provider]) {
      this.renderOptions(this.cache[provider], preferred)
      return
    }

    // No endpoint (e.g. an unsaved team): use the static options as-is.
    if (!this.modelsUrlValue) {
      this.renderOptions(this.staticOptions[provider] || [], preferred)
      return
    }

    this.setLoading(true)
    try {
      const url = `${this.modelsUrlValue}?provider=${encodeURIComponent(provider)}`
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      const models = Array.isArray(data.models) ? data.models : []
      const normalized = models
        .filter((m) => m && m.id)
        .map((m) => ({ id: String(m.id), label: String(m.label || m.id) }))
      this.cache[provider] = normalized.length ? normalized : (this.staticOptions[provider] || [])
    } catch (error) {
      // Network/parse failure → fall back to the static catalogue.
      this.cache[provider] = this.staticOptions[provider] || []
    } finally {
      this.setLoading(false)
      this.renderOptions(this.cache[provider], preferred)
    }
  }

  renderOptions(models, preferred) {
    const select = this.modelTarget
    const previous = select.value
    select.innerHTML = ""

    models.forEach((model) => {
      const option = document.createElement("option")
      option.value = model.id
      option.text = model.label
      select.appendChild(option)
    })

    const ids = models.map((m) => m.id)
    if (preferred && ids.includes(preferred)) {
      select.value = preferred
    } else if (ids.includes(previous)) {
      select.value = previous
    } else if (ids.length) {
      select.value = ids[0]
    }
  }

  setLoading(loading) {
    this.modelTarget.disabled = loading
  }
}
